import { useEffect, useState } from 'react'
import Cta from '@/components/storefront/Cta'
import Modal from '@/components/ui/Modal'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { useToast } from '@/components/ui/toast'
import { fieldErrors, errorMessage } from '@/lib/api'
import { useSaveAddress, useShippingInfo, type AddressForm } from '@/lib/addresses'
import type { CustomerAddress } from '@/types'

const EMPTY: AddressForm = {
  label: 'Home',
  recipient_name: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  state: '',
  is_default: false,
}

/**
 * Add / edit one address-book entry. The state list comes from the API so it
 * always matches what the backend accepts, and server-side field errors land
 * under their fields.
 */
export default function AddressModal({
  open,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean
  initial: CustomerAddress | null
  onClose: () => void
  onSaved: (address: CustomerAddress) => void
}) {
  const toast = useToast()
  const shippingInfo = useShippingInfo()
  const save = useSaveAddress()

  const [form, setForm] = useState<AddressForm>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string[]>>({})

  useEffect(() => {
    if (!open) return
    setErrors({})
    setForm(
      initial
        ? {
            label: initial.label ?? '',
            recipient_name: initial.recipient_name,
            phone: initial.phone,
            line1: initial.line1,
            line2: initial.line2 ?? '',
            city: initial.city,
            postcode: initial.postcode,
            state: initial.state,
            is_default: initial.is_default,
          }
        : EMPTY,
    )
  }, [open, initial])

  const set = (key: keyof AddressForm) => (value: string) => setForm((prev) => ({ ...prev, [key]: value }))
  const firstError = (key: string) => errors[key]?.[0]

  const submit = () => {
    setErrors({})
    save.mutate(
      { id: initial?.id, form },
      {
        onSuccess: (address) => {
          toast.success(initial ? 'Address updated' : 'Address saved')
          onSaved(address)
          onClose()
        },
        onError: (error) => {
          const fields = fieldErrors(error)
          if (Object.keys(fields).length > 0) setErrors(fields)
          else toast.error('Could not save address', errorMessage(error))
        },
      },
    )
  }

  return (
    <Modal open={open} onClose={onClose} title={initial ? 'Edit address' : 'Add address'} width="max-w-lg">
      {/* The form is taller than a phone screen; it scrolls inside the modal
          so the title and Save button stay visible. Short fields pair up
          below sm too, keeping the mobile form compact. */}
      <div className="grid max-h-[60vh] grid-cols-2 content-start gap-3 overflow-y-auto pr-1 sm:gap-4">
        <Field label="Recipient name / company" error={firstError('recipient_name')} className="col-span-2">
          <Input
            value={form.recipient_name}
            onChange={(e) => set('recipient_name')(e.target.value)}
            placeholder="Full name or company name"
            autoFocus
          />
        </Field>
        <Field label="Phone number" error={firstError('phone')}>
          <Input
            value={form.phone}
            onChange={(e) => set('phone')(e.target.value)}
            placeholder="012-345 6789"
            inputMode="tel"
          />
        </Field>
        <Field label="Label" error={firstError('label')}>
          <Select
            value={form.label ?? ''}
            onChange={set('label')}
            options={['Home', 'Office', 'Other'].map((v) => ({ value: v, label: v }))}
          />
        </Field>
        <Field label="Street address" error={firstError('line1')} className="col-span-2">
          <Input value={form.line1} onChange={(e) => set('line1')(e.target.value)} placeholder="House number and street" />
        </Field>
        <Field label="Street address line 2 (optional)" error={firstError('line2')} className="col-span-2">
          <Input value={form.line2 ?? ''} onChange={(e) => set('line2')(e.target.value)} placeholder="Apartment, taman, landmark..." />
        </Field>
        <Field label="Postcode" error={firstError('postcode')}>
          <Input
            value={form.postcode}
            onChange={(e) => set('postcode')(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="34300"
            inputMode="numeric"
          />
        </Field>
        <Field label="City / town" error={firstError('city')}>
          <Input value={form.city} onChange={(e) => set('city')(e.target.value)} />
        </Field>
        <Field label="State" error={firstError('state')}>
          <Select
            value={form.state}
            onChange={set('state')}
            placeholder="Select state..."
            options={(shippingInfo.data?.states ?? []).map((s) => ({ value: s, label: s }))}
          />
        </Field>
        <Field label="Country">
          <Input value="Malaysia" disabled readOnly />
        </Field>
        <label className="col-span-2 flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(form.is_default)}
            onChange={(e) => setForm((prev) => ({ ...prev, is_default: e.target.checked }))}
            className="size-4 accent-primary"
          />
          Set as my default address
        </label>
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        <button type="button" onClick={onClose} className="text-sm font-semibold text-foreground/60 hover:text-foreground">
          Cancel
        </button>
        <Cta size="md" onClick={submit} loading={save.isPending}>
          Save address
        </Cta>
      </div>
    </Modal>
  )
}
